import { CopyIcon } from '@phosphor-icons/react/Copy';
import {
  type LessonItem,
  type SpreadMode,
  spreadTopics,
  todayIso,
  topicsInOrder,
  useCourseLessons,
  useCourses,
  useSetTopics,
  useTerms,
} from '@shkola/api';
import { programTopics } from '@shkola/core';
import { subjectLook } from '@shkola/design';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { LessonBadges, LessonPlanModal } from '../components/lessons';
import { Select, Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  Tabs,
  grad,
  pillClass,
} from '../design/forms';
import { BookOpenTextIcon, CheckIcon, PencilSimpleIcon } from '../design/icons';
import { monthName, weekdayShort } from '../student/home/model';

/*
 * «План уроків» in «Форми 2» (28.10): the title on the paper with the course and the term; on the left
 * where the topics come from (the sample programme, a parallel class, a pasted list), how to lay them
 * and the buttons; on the right the term's lessons by month — the date as a small tile, the topic (a new
 * topic in full ink, its continuation softer), the marks, what changed in the draft.
 */

/** Lessons of a course for a term, with the topics of the plan; the copy source loads the same way. */
function useTermLessons(
  schoolId: string,
  courseId: string,
  term?: { starts_on: string; ends_on: string },
) {
  return useCourseLessons(schoolId, courseId, term?.starts_on ?? '', term?.ends_on ?? '');
}

/** A choice of many (the ways to lay the topics) as soft chips of the paper, as in «Домашні». */
const chip = (on: boolean) =>
  `inline-flex h-9 shrink-0 items-center rounded-full px-3.5 text-[13.5px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`;

/**
 * «План уроків»: the teacher lays topics over a term's lessons — from a sample programme, from a
 * parallel class or a pasted list — sees it on the calendar and saves the whole plan at once.
 */
export function PlanningPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const courses = useCourses({ teacherMemberId: ctx.memberId });
  const terms = useTerms(ctx.schoolId);
  const today = todayIso();
  const [courseId, setCourseId] = useState('');
  const [termId, setTermId] = useState('');
  const list = courses.data ?? [];
  const course = list.find((c) => c.id === courseId) ?? list[0];
  const allTerms = terms.data ?? [];
  const term =
    allTerms.find((x) => x.id === termId) ??
    allTerms.find((x) => x.starts_on <= today && today <= x.ends_on) ??
    allTerms[0];
  const lessons = useTermLessons(ctx.schoolId, course?.id ?? '', term);
  // the same subject in another class of the same grade, for «Як у 7-А»
  const parallel = list.find(
    (c) => course && c.id !== course.id && c.subject === course.subject && c.grade === course.grade,
  );
  const parallelLessons = useTermLessons(ctx.schoolId, parallel?.id ?? '', term);

  const [text, setText] = useState('');
  const [mode, setMode] = useState<SpreadMode>({ kind: 'even' });
  const [draft, setDraft] = useState<Map<string, string | null>>();
  const [open, setOpen] = useState<LessonItem>();
  const [saved, setSaved] = useState<number>();
  const save = useSetTopics();
  const all = lessons.data ?? [];

  const fill = (topics: readonly string[]) => {
    setText(topics.join('\n'));
    setDraft(undefined);
    setSaved(undefined);
  };
  const shown = useMemo(
    () =>
      all.map((l) => ({
        lesson: l,
        topic: draft?.has(l.id) ? (draft.get(l.id) ?? null) : l.topic,
        changed: draft?.has(l.id) === true && draft.get(l.id) !== l.topic,
      })),
    [all, draft],
  );

  if (courses.isPending || terms.isPending) return <Spinner />;
  if (!course) {
    return (
      <div className="flex flex-col gap-4">
        <PageHead title={t('planning.title')} />
        <Block>
          <p className="py-6 text-center text-[15px]" style={{ color: INK_SOFT }}>
            {t('planning.noCourses')}
          </p>
        </Block>
      </div>
    );
  }
  const look = subjectLook(course.subject);
  const program = programTopics(course.subject);
  const canCopy = parallel && (parallelLessons.data ?? []).some((l) => l.topic);
  const modes: ReadonlyArray<{ on: boolean; mode: SpreadMode; label: string }> = [
    { on: mode.kind === 'even', mode: { kind: 'even' }, label: t('planning.even') },
    ...[2, 3, 4].map((n) => ({
      on: mode.kind === 'per' && mode.lessons === n,
      mode: { kind: 'per' as const, lessons: n },
      label: t('planning.per', { n }),
    })),
  ];
  const changedCount = shown.filter((s) => s.changed).length;

  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('planning.title')} sub={t('planning.intro')}>
        <div className="flex max-w-[calc(100vw-32px)] flex-wrap items-center gap-2">
          <Select
            aria-label={t('planning.course')}
            className="!h-11 max-w-full !rounded-full !pr-3 !pl-4"
            value={course.id}
            onChange={(e) => {
              setCourseId(e.target.value);
              setDraft(undefined);
              setText('');
            }}
          >
            {list.map((c) => (
              <option key={c.id} value={c.id}>
                {c.subject}
                {c.groupLabel ? ` (${c.groupLabel})` : ''} · {c.classLabel}
              </option>
            ))}
          </Select>
          {allTerms.length > 1 && (
            <Tabs
              label={t('planning.term')}
              value={term?.id}
              onChange={(id) => {
                setTermId(id);
                setDraft(undefined);
              }}
              options={allTerms.map((x) => ({
                id: x.id,
                label: t('planning.termN', { n: x.number }),
              }))}
            />
          )}
        </div>
      </PageHead>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
        <Block className="lg:sticky lg:top-20">
          <Head title={t('planning.source')} />
          {(program.length > 0 || canCopy) && (
            <div className="-mt-2 mb-5 flex flex-wrap gap-2">
              {program.length > 0 && (
                <button
                  type="button"
                  onClick={() => fill(program)}
                  className={pillClass('light', 'sm')}
                >
                  {t('planning.program')}
                  <PillIcon icon={BookOpenTextIcon} size="sm" />
                </button>
              )}
              {canCopy && (
                <button
                  type="button"
                  onClick={() => fill(topicsInOrder(parallelLessons.data ?? []))}
                  className={pillClass('light', 'sm')}
                >
                  {t('planning.copyFrom', { class: parallel.classLabel })}
                  <PillIcon icon={CopyIcon} size="sm" />
                </button>
              )}
            </div>
          )}
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium" style={{ color: INK_SOFT }}>
              {t('planning.topics')}
            </span>
            <textarea
              rows={10}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setSaved(undefined);
              }}
              className="w-full resize-y rounded-[16px] bg-white p-3.5 text-[14.5px] leading-6 outline-none ring-1 ring-[#E4E0D7] ring-inset focus:ring-2 focus:ring-[var(--sh-dark)]"
              style={{ color: INK }}
            />
          </label>
          <div
            className="mt-3 flex flex-wrap gap-1.5"
            role="radiogroup"
            aria-label={t('planning.preview')}
          >
            {modes.map((m) => (
              <button
                key={m.label}
                type="button"
                role="radio"
                aria-checked={m.on}
                onClick={() => setMode(m.mode)}
                className={chip(m.on)}
                style={{ backgroundColor: m.on ? '#E9E5DC' : PAPER, color: INK }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!text.trim() || all.length === 0}
              onClick={() => {
                setDraft(spreadTopics(all, text.split('\n'), mode));
                setSaved(undefined);
              }}
              className="inline-flex h-11 items-center rounded-full bg-white px-4.5 text-[14px] font-medium ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#FAF9F6] disabled:opacity-40"
              style={{ color: INK }}
            >
              {t('planning.preview')}
            </button>
            <button
              type="button"
              disabled={!draft || draft.size === 0 || save.isPending}
              onClick={() =>
                draft &&
                void save.mutateAsync(draft).then((n) => {
                  setSaved(n);
                  setDraft(undefined);
                })
              }
              className={`${pillClass('dark')} disabled:opacity-35`}
            >
              {t('planning.save')}
              <PillIcon icon={CheckIcon} />
            </button>
            {draft && (
              <button
                type="button"
                onClick={() => setDraft(undefined)}
                className="px-2 text-[14px] font-medium underline underline-offset-4"
                style={{ color: INK_SOFT }}
              >
                {t('planning.reset')}
              </button>
            )}
          </div>
          {saved !== undefined && (
            <p
              className="mt-3 inline-flex items-center gap-1.5 self-start rounded-[8px] px-2.5 py-1.5 text-[13px] font-medium"
              style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
            >
              <CheckIcon size={13} weight="bold" aria-hidden />
              {t('planning.saved', { count: saved })}
            </p>
          )}
        </Block>

        <Block>
          <Head
            title={course.subject}
            note={`${course.classLabel} · ${t('planning.lessons', { count: all.length })}`}
          >
            {changedCount > 0 && (
              <span
                className="rounded-[8px] px-2.5 py-1.5 text-[12.5px] leading-none font-medium"
                style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
              >
                {t('planning.changed')} · {changedCount}
              </span>
            )}
            <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={40} />
          </Head>
          {lessons.isPending ? (
            <Spinner />
          ) : all.length === 0 ? (
            <p className="py-6 text-center text-[15px]" style={{ color: INK_SOFT }}>
              {t('planning.empty')}
            </p>
          ) : (
            <ol className="flex flex-col">
              {shown.map(({ lesson, topic, changed }, i) => {
                const prev = shown[i - 1];
                const newTopic = topic && topic !== prev?.topic;
                const month = lesson.date.slice(0, 7);
                const newMonth = month !== prev?.lesson.date.slice(0, 7);
                const isToday = lesson.date === today;
                return (
                  <Fragment key={lesson.id}>
                    {newMonth && (
                      <li
                        className={`pb-1.5 text-[13px] font-medium ${i > 0 ? 'pt-5' : ''}`}
                        style={{ color: INK_FAINT }}
                      >
                        {monthName(lesson.date)}
                      </li>
                    )}
                    <li
                      className={lesson.date < today ? 'opacity-55' : ''}
                      style={{ borderTop: newMonth ? undefined : `1px solid ${LINE}` }}
                    >
                      <button
                        type="button"
                        onClick={() => setOpen(lesson)}
                        title={t('planning.details')}
                        className="group -mx-2 flex w-[calc(100%+16px)] items-center gap-3 rounded-[14px] px-2 py-2 text-left transition-colors hover:bg-[#FAF9F6]"
                      >
                        <span
                          className="flex size-11 shrink-0 flex-col items-center justify-center rounded-[12px] leading-none"
                          style={
                            isToday
                              ? { backgroundImage: grad('orange'), color: '#FFFFFF' }
                              : { backgroundColor: PAPER, color: INK }
                          }
                        >
                          <span
                            className="text-[10px] font-medium"
                            style={{ color: isToday ? '#FFFFFF' : INK_SOFT }}
                          >
                            {weekdayShort(lesson.date)}
                          </span>
                          <span className="mt-0.5 text-[15px] font-medium">
                            {Number(lesson.date.slice(8))}
                          </span>
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={`block truncate text-[15px] leading-[21px] ${newTopic ? 'font-medium' : ''}`}
                            style={{ color: topic ? (newTopic ? INK : INK_SOFT) : INK_FAINT }}
                            title={topic ?? undefined}
                          >
                            {topic ?? t('planning.noTopic')}
                          </span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-1 empty:hidden">
                            <LessonBadges lesson={lesson} />
                          </span>
                        </span>
                        {changed && (
                          <span
                            className="shrink-0 rounded-[7px] px-2 py-1 text-[11.5px] leading-none font-medium"
                            style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
                          >
                            {t('planning.changed')}
                          </span>
                        )}
                        <PencilSimpleIcon
                          size={15}
                          weight="bold"
                          aria-label={t('planning.details')}
                          className="shrink-0 text-[var(--sh-ink-faint)] transition-colors group-hover:text-[var(--sh-ink)]"
                        />
                      </button>
                    </li>
                  </Fragment>
                );
              })}
            </ol>
          )}
        </Block>
      </div>
      {open && <LessonPlanModal lesson={open} onClose={() => setOpen(undefined)} />}
    </div>
  );
}
